import {Sheet, SheetContent, SheetHeader, SheetTitle} from "@/components/ui/sheet";
import {Loader} from "@/src/modules/core/components";
import type {AmenityRestrictionCheckSource} from "@/src/modules/households/lib/household-amenity-restriction";

import {useResidentHomePropertyAmenities} from "../hooks/use-resident-home-property-amenities";
import {getResidentFacilitiesRestrictionMessage} from "../lib/resident-amenity-restriction-copy";

import {ResidentHomeAmenitiesPreviewRow} from "./resident-home-amenities-preview-row";
import {ResidentAmenityRestrictionNotice} from "./resident-amenity-restriction-notice";

const EXPAND_LIMIT = 10;

type ResidentHomeAmenitiesSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  household?: AmenityRestrictionCheckSource | null;
};

export function ResidentHomeAmenitiesSheet({
  open,
  onOpenChange,
  household,
}: ResidentHomeAmenitiesSheetProps) {
  const facilitiesRestrictionMessage = getResidentFacilitiesRestrictionMessage(household);

  const {amenities, totalRecords, canShow, isPending, isError} = useResidentHomePropertyAmenities({
    enabled: open,
    limit: EXPAND_LIMIT,
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex min-h-0 flex-col gap-0 p-0 sm:max-w-md">
        <SheetHeader className="border-border flex flex-col gap-2 border-b">
          <SheetTitle>Pool & amenities</SheetTitle>
          {facilitiesRestrictionMessage ? (
            <ResidentAmenityRestrictionNotice message={facilitiesRestrictionMessage} />
          ) : null}
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {!canShow ? (
            <p className="text-muted-foreground text-sm">
              You do not have permission to view amenities.
            </p>
          ) : isPending ? (
            <Loader className="min-h-48" />
          ) : isError ? (
            <p className="text-muted-foreground text-sm">Unable to load amenities.</p>
          ) : amenities.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No amenities configured for your property.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {amenities.map((amenity) => (
                <ResidentHomeAmenitiesPreviewRow
                  key={amenity.property_amenity_id}
                  amenity={amenity}
                  household={household}
                />
              ))}
              {totalRecords > EXPAND_LIMIT ? (
                <p className="text-muted-foreground pt-2 text-center text-xs">
                  Showing {EXPAND_LIMIT} of {totalRecords}.
                </p>
              ) : null}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
